import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Upload, CheckCircle, XCircle, AlertTriangle, FileText, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Manager Validation Upload — Phase 11.7
 *
 * Two modes:
 *   - Mode A: blank MV template (reusable across candidates)
 *   - Mode B: completed MV form for one specific candidate (response ingestion)
 *
 * Flow per upload:
 *   1. Pick mode + file (+ candidate for Mode B) → POST /mv/upload-{template|completed}
 *   2. Poll /uploaded/{id}/parse-result until status leaves "pending"/"parsing"
 *   3. Show side-by-side review screen (raw text on left, parsed canonical on right)
 *   4. Operator approves or edits or rejects
 */
export default function MVUploadTab() {
    const { api, user } = useAuth();
    const wsId = user?.enterprise_id;
    const [uploads, setUploads] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [showUploader, setShowUploader] = useState(false);

    const refresh = async () => {
        try {
            const r = await api.get(`/v2/workspaces/${wsId}/mv/uploaded`);
            setUploads(r.data.uploads || []);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => { if (wsId) refresh(); }, [wsId]);

    if (selectedId) {
        return <MVParseReviewScreen fileId={selectedId} onBack={() => { setSelectedId(null); refresh(); }} />;
    }

    return (
        <div className="space-y-5" data-testid="mv-upload-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-base font-display font-bold text-white">Manager Validation uploads</h2>
                    <p className="text-xs text-gray-500 font-body mt-1">
                        Upload your own MV form (blank template) or ingest completed MV responses your managers filled out offline.
                    </p>
                </div>
                <button onClick={() => setShowUploader(true)}
                    className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200"
                    data-testid="open-mv-uploader-btn">
                    Upload MV form
                </button>
            </div>

            <div className="bg-card border border-border/40">
                <div className="px-5 py-3 border-b border-border/40">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-mono">Recent uploads · {uploads.length}</p>
                </div>
                {uploads.length === 0 ? (
                    <p className="px-5 py-10 text-center text-xs text-gray-500 font-body">No MV uploads yet.</p>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-gray-500 uppercase tracking-wider font-mono">
                            <tr className="border-b border-border/30">
                                <th className="text-left py-2 px-5">File</th>
                                <th className="text-left py-2">Mode</th>
                                <th className="text-left py-2">Status</th>
                                <th className="text-left py-2">Confidence</th>
                                <th className="text-left py-2">Uploaded</th>
                                <th className="py-2 px-5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {uploads.map(u => (
                                <tr key={u.id} className="border-b border-border/20" data-testid={`mv-upload-row-${u.id}`}>
                                    <td className="py-2.5 px-5 text-white">{u.file_name}</td>
                                    <td className="py-2.5 text-gray-400 font-mono">
                                        {u.upload_target_module === "mv_template" ? "A · Template" : "B · Completed"}
                                    </td>
                                    <td className="py-2.5"><StatusBadge status={u.parse_status} /></td>
                                    <td className="py-2.5 text-gray-400 font-mono">{u.parse_confidence != null ? `${Math.round(u.parse_confidence)}%` : "—"}</td>
                                    <td className="py-2.5 text-gray-500 font-mono">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : ""}</td>
                                    <td className="py-2.5 px-5 text-right">
                                        <button onClick={() => setSelectedId(u.id)}
                                            className="text-xs font-mono uppercase tracking-wider border border-border/40 text-gray-300 hover:text-white px-3 py-1"
                                            data-testid={`mv-review-btn-${u.id}`}>
                                            Review
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showUploader && (
                <MVUploadModal api={api} wsId={wsId}
                    onClose={() => setShowUploader(false)}
                    onUploaded={(fileId) => { setShowUploader(false); refresh(); setSelectedId(fileId); }} />
            )}
        </div>
    );
}


function StatusBadge({ status }) {
    const map = {
        pending: { c: "text-gray-400 border-gray-600", l: "Pending" },
        parsing: { c: "text-amber-300 border-amber-500/40", l: "Parsing…" },
        review_required: { c: "text-amber-300 border-amber-500/40", l: "Review required" },
        approved: { c: "text-emerald-300 border-emerald-500/40", l: "Approved" },
        rejected: { c: "text-red-300 border-red-500/40", l: "Rejected" },
    };
    const v = map[status] || { c: "text-gray-400 border-gray-600", l: status };
    return <span className={`text-xs font-mono uppercase px-2 py-0.5 border ${v.c}`}>{v.l}</span>;
}


function MVUploadModal({ api, wsId, onClose, onUploaded }) {
    const [mode, setMode] = useState("mv_template");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [candidateId, setCandidateId] = useState("");
    const [managerEmail, setManagerEmail] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        // Load candidates so Mode B has a dropdown
        api.get(`/v2/workspaces/${wsId}/candidates/distribution-status`)
            .then(r => setCandidates(r.data.candidates || []))
            .catch(() => {});
    }, [api, wsId]);

    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { setError("Please choose a file."); return; }
        if (mode === "mv_completed_response" && !candidateId) {
            setError("Pick the candidate this MV is about.");
            return;
        }
        setError(null); setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        if (mode === "mv_completed_response") {
            fd.append("candidate_id", candidateId);
            if (managerEmail) fd.append("manager_email", managerEmail);
        }
        try {
            const endpoint = mode === "mv_template"
                ? `/v2/workspaces/${wsId}/mv/upload-template`
                : `/v2/workspaces/${wsId}/mv/upload-completed`;
            const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded — parsing now…");
            onUploaded(r.data.file_id);
        } catch (e) {
            setError(e.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-bg border border-border w-full max-w-xl" onClick={e => e.stopPropagation()}
                data-testid="mv-upload-modal">
                <div className="flex items-baseline justify-between p-5 border-b border-border/40">
                    <h3 className="text-base font-display font-bold text-white">Upload Manager Validation form</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Mode toggle */}
                    <div className="space-y-2">
                        <label className="text-xs text-gray-500 uppercase tracking-wider font-mono block">Mode</label>
                        <button onClick={() => setMode("mv_template")}
                            className={`w-full text-left p-3 border ${mode === "mv_template" ? "border-white bg-muted" : "border-border/40 hover:border-border"}`}
                            data-testid="mv-mode-template">
                            <p className="text-sm text-white">Upload a blank MV form (reusable)</p>
                            <p className="text-xs text-gray-500 mt-1 font-body">Available in the assessment library to assign to any candidate.</p>
                        </button>
                        <button onClick={() => setMode("mv_completed_response")}
                            className={`w-full text-left p-3 border ${mode === "mv_completed_response" ? "border-white bg-muted" : "border-border/40 hover:border-border"}`}
                            data-testid="mv-mode-completed">
                            <p className="text-sm text-white">Upload a completed MV for one specific candidate</p>
                            <p className="text-xs text-gray-500 mt-1 font-body">Manager has already filled it out offline. Responses ingested directly.</p>
                        </button>
                    </div>

                    {/* File */}
                    <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider font-mono block mb-1">File</label>
                        <input ref={fileRef} type="file" accept=".docx,.pdf,.md,.txt,.html,.json"
                            className="w-full text-xs text-gray-300 bg-muted border border-border/40 px-3 py-2"
                            data-testid="mv-upload-file" />
                        <p className="text-xs text-gray-600 mt-1 font-body">.docx · .pdf · .md · .txt · .html · .json — up to 10 MB</p>
                    </div>

                    {/* Mode B fields */}
                    {mode === "mv_completed_response" && (
                        <>
                            <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wider font-mono block mb-1">Candidate *</label>
                                <select value={candidateId} onChange={e => setCandidateId(e.target.value)}
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                    data-testid="mv-candidate-select">
                                    <option value="">— Select —</option>
                                    {candidates.map((c, i) => (
                                        <option key={c.id || c.candidate_id || i} value={c.id || c.candidate_id}>{c.full_name} — {c.email}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wider font-mono block mb-1">Manager email (optional override)</label>
                                <input value={managerEmail} onChange={e => setManagerEmail(e.target.value)}
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                    placeholder="Defaults to candidate.manager_email"
                                    data-testid="mv-manager-email" />
                            </div>
                        </>
                    )}

                    {error && <p className="text-xs text-red-300" data-testid="mv-upload-error">{error}</p>}
                </div>

                <div className="flex justify-end gap-3 p-5 border-t border-border/40">
                    <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-white">Cancel</button>
                    <button onClick={submit} disabled={busy}
                        className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200 disabled:opacity-40"
                        data-testid="mv-upload-submit">
                        {busy ? "Uploading…" : "Upload & parse"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function MVParseReviewScreen({ fileId, onBack }) {
    const { api, user } = useAuth();
    const wsId = user?.enterprise_id;
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);

    const fetchOnce = async () => {
        try {
            const r = await api.get(`/v2/workspaces/${wsId}/mv/uploaded/${fileId}/parse-result`);
            setData(r.data);
            if (r.data.parse_status === "approved" || r.data.parse_status === "rejected" || r.data.parse_status === "review_required") {
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchOnce();
        pollRef.current = setInterval(fetchOnce, 4000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/v2/workspaces/${wsId}/mv/uploaded/${fileId}/approve`, {});
            toast.success("Approved");
            onBack();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Approve failed.");
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        const reason = prompt("Reason for rejecting this parse?");
        if (!reason) return;
        setBusy(true);
        try {
            await api.post(`/v2/workspaces/${wsId}/mv/uploaded/${fileId}/reject`, { reason });
            toast.success("Rejected");
            onBack();
        } catch (e) {
            toast.error("Reject failed.");
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return <div className="text-sm text-gray-500" data-testid="mv-review-loading">Loading parse result…</div>;
    }

    const schema = (data.parse_output || {}).schema || {};
    const responses = (data.parse_output || {}).responses;
    const scaleConv = (data.parse_output || {}).scale_conversion;
    const dims = schema.values_dimensions || {};
    const rats = schema.rationale_fields || {};
    const anchors = schema.capability_anchors || {};
    const tasks = schema.task_specific || {};

    const isParsing = data.parse_status === "pending" || data.parse_status === "parsing";

    return (
        <div className="space-y-4" data-testid="mv-parse-review-screen">
            <div className="flex items-center justify-between">
                <button onClick={onBack} className="text-xs text-gray-400 hover:text-white" data-testid="mv-back-btn">← Back to uploads</button>
                <div className="flex items-center gap-3">
                    <p className="text-xs text-gray-500 font-mono">{data.file_name}</p>
                    <StatusBadge status={data.parse_status} />
                    {data.parse_confidence != null && (
                        <ConfidenceBadge value={data.parse_confidence} />
                    )}
                </div>
            </div>

            {isParsing && (
                <div className="bg-card border border-border/40 p-12 text-center">
                    <div className="w-10 h-10 border-2 border-emerald-300/40 border-t-emerald-300 rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-400">Parsing your MV form — typically 5-15 seconds.</p>
                </div>
            )}

            {data.parse_status === "rejected" && (
                <div className="bg-red-500/10 border border-red-500/30 p-5" data-testid="mv-rejected-banner">
                    <p className="text-sm text-red-200 font-bold">Parse rejected</p>
                    <p className="text-xs text-red-200/80 mt-1 font-body">{data.parse_error}</p>
                </div>
            )}

            {!isParsing && data.parse_status !== "rejected" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Left: raw text */}
                    <div className="bg-card border border-border/40 max-h-[70vh] overflow-auto">
                        <div className="px-5 py-3 border-b border-border/40 sticky top-0 bg-card">
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-mono flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5" /> Original upload
                            </p>
                        </div>
                        <pre className="p-5 text-xs text-gray-300 font-body whitespace-pre-wrap leading-relaxed" data-testid="mv-raw-text">
                            {data.raw_text_preview}
                        </pre>
                    </div>

                    {/* Right: parsed structure */}
                    <div className="bg-card border border-border/40 max-h-[70vh] overflow-auto">
                        <div className="px-5 py-3 border-b border-border/40 sticky top-0 bg-card">
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-mono">Parsed canonical structure</p>
                        </div>
                        <div className="p-5 space-y-5">
                            {/* Values dimensions */}
                            <Section title="Values dimension ratings" confidence={dims.confidence}>
                                {(dims.mapped_dimensions || []).map((m, i) => (
                                    <div key={i} className="flex items-baseline gap-2 text-xs" data-testid={`mv-dim-row-${i}`}>
                                        <span className="text-white">{m.canonical_dimension}</span>
                                        <span className="text-gray-500">←</span>
                                        <span className="text-gray-400 italic">"{m.upload_label}"</span>
                                        <ConfidenceBadge value={m.confidence} mini />
                                    </div>
                                ))}
                                {(dims.unmapped_upload_labels || []).length > 0 && (
                                    <p className="text-xs text-amber-300 mt-2">Unmapped labels: {dims.unmapped_upload_labels.join(", ")}</p>
                                )}
                                {(dims.missing_canonical_dimensions || []).length > 0 && (
                                    <p className="text-xs text-amber-300 mt-1">Workspace dimensions not present in upload: {dims.missing_canonical_dimensions.join(", ")}</p>
                                )}
                            </Section>

                            {/* Rationale */}
                            <Section title="Free-text rationale fields" confidence={rats.confidence}>
                                <p className="text-xs text-gray-300">Per-dimension narrative: {rats.per_dimension_narrative_present ? "✓ present" : "✗ missing"}</p>
                                <p className="text-xs text-gray-300">Overall comment: {rats.overall_comment_present ? "✓ present" : "✗ missing"}</p>
                            </Section>

                            {/* Capability anchors */}
                            {(anchors.questions || []).length > 0 && (
                                <Section title="Capability anchor questions" confidence={anchors.confidence}>
                                    {anchors.questions.map((q, i) => (
                                        <p key={i} className="text-xs text-gray-300">• {q.prompt} <span className="text-gray-600 font-mono">({q.answer_type})</span></p>
                                    ))}
                                </Section>
                            )}

                            {/* Task specific */}
                            {(tasks.questions || []).length > 0 && (
                                <Section title="Task-specific questions" confidence={tasks.confidence}>
                                    {tasks.questions.map((q, i) => (
                                        <p key={i} className="text-xs text-gray-300">• {q.prompt} <span className="text-gray-600 font-mono">({q.answer_type})</span></p>
                                    ))}
                                </Section>
                            )}

                            {/* Scale conversion preview */}
                            {scaleConv && (
                                <Section title={`Scale conversion: ${scaleConv.upload_min}–${scaleConv.upload_max} → 1–5`} confidence={null}>
                                    <table className="text-xs w-full">
                                        <tbody>
                                            {scaleConv.preview.map(p => (
                                                <tr key={p.upload_value} className="border-b border-border/20">
                                                    <td className="py-1 text-gray-400 font-mono">Upload {p.upload_value}</td>
                                                    <td className="py-1 text-gray-500">→</td>
                                                    <td className="py-1 text-white font-mono">Realloc {p.canonical_1_5} <span className="text-gray-600">({p.normalized_float})</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Section>
                            )}

                            {/* Mode B: extracted responses */}
                            {responses && (
                                <Section title="Manager's extracted responses (Mode B)" confidence={responses.extraction_confidence}>
                                    {(responses.values_ratings || []).map((r, i) => (
                                        <p key={i} className="text-xs text-gray-300">
                                            <span className="text-white">{r.canonical_dimension}</span>: rating {r.rating_raw} (normalized {r.rating_normalized_1_5}/5)
                                        </p>
                                    ))}
                                    {responses.overall_comment && (
                                        <p className="text-xs text-gray-400 mt-2 italic">"{responses.overall_comment.slice(0, 200)}"</p>
                                    )}
                                </Section>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Actions */}
            {!isParsing && data.parse_status !== "rejected" && data.parse_status !== "approved" && (
                <div className="flex justify-end gap-3 sticky bottom-4">
                    <button onClick={reject} disabled={busy}
                        className="text-xs font-mono uppercase tracking-wider border border-red-500/40 text-red-300 hover:bg-red-500/10 px-5 py-2 disabled:opacity-40"
                        data-testid="mv-reject-btn">
                        Reject & re-upload
                    </button>
                    <button onClick={approve} disabled={busy}
                        className="text-xs font-mono uppercase tracking-wider bg-white text-black px-5 py-2 hover:bg-gray-200 disabled:opacity-40"
                        data-testid="mv-approve-btn">
                        {busy ? "Approving…" : "Approve mappings"}
                    </button>
                </div>
            )}
        </div>
    );
}


function Section({ title, confidence, children }) {
    return (
        <div className="border-l-2 border-border/40 pl-4">
            <div className="flex items-baseline justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-mono">{title}</p>
                {confidence != null && <ConfidenceBadge value={confidence} mini />}
            </div>
            <div className="space-y-1">{children}</div>
        </div>
    );
}


function ConfidenceBadge({ value, mini }) {
    const v = Math.round(value || 0);
    const tone = v >= 80 ? "text-emerald-300 border-emerald-500/40" :
                 v >= 60 ? "text-amber-300 border-amber-500/40" :
                 "text-red-300 border-red-500/40";
    const cls = mini ? "text-[10px] px-1.5 py-0" : "text-xs px-2 py-0.5";
    return <span className={`font-mono uppercase border ${cls} ${tone}`} data-testid="mv-confidence-badge">{v}%</span>;
}
